"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {getWorkflow} from "@/actions/workflows/actions";
import type {SavedOmegaWorkflow} from "@/services/wingman/omega-results";
import {confirmedDraftOf} from "./useChatWorkflowPublication";

/** History identifies the workflow; Omega owns its current saved content and status. */
export function useCurrentChatWorkflow(history: SavedOmegaWorkflow) {
   // Conversation projections recreate objects on each streamed chunk. Only a
   // different saved result should invalidate the current workflow.
   const source = JSON.stringify(history);
   const [current, setCurrent] = useState<{source: string; workflow: SavedOmegaWorkflow | null}>();
   const [attempt, setAttempt] = useState(0);
   const request = useRef(0);
   const latestSource = useRef(source);
   latestSource.current = source;

   useEffect(() => {
      const version = ++request.current;
      void getWorkflow(history.id).catch(() => null).then(workflow => {
         if (version !== request.current) return;
         setCurrent({source, workflow: workflow?.status ? {...confirmedDraftOf(workflow), status: workflow.status} : null});
      });
      return () => { request.current++; };
   }, [history.id, source, attempt]);

   const update = useCallback((workflow: SavedOmegaWorkflow) => {
      // A completed publication for an older save must not overwrite or cancel
      // the current save's status lookup.
      if (latestSource.current !== source) return;
      // A read started before publication must not overwrite its result.
      request.current++;
      setCurrent({source, workflow});
   }, [source]);
   const retry = () => {
      setCurrent(undefined);
      setAttempt(value => value + 1);
   };
   const resolved = current?.source === source;
   return {
      workflow: resolved && current.workflow ? current.workflow : history,
      loading: !resolved,
      failed: resolved && !current.workflow,
      update,
      retry,
   };
}
